import { useDrawerEscape } from '../../lib/hooks/useDrawerEscape';
import { coreCall } from '../../lib/services/gatewayService';
import { customerOrderSettings } from '../../lib/utils/customerOrderSettings';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { usePermissions } from '../../lib/hooks/usePermissions';
/**
 * ManualOrderCreateModal
 * Modal for creating new manual orders
 * Supports client selection, product selection with availability
 *
 * NB: non importa piu' le righe da un'offerta. Quel percorso si imbocca dal lato
 * opposto — il riquadro "Ordine di magazzino" nel dettaglio offerta — che passa
 * da flusia-core /api/orders/from-deal, unica implementazione della regola.
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  FileText,
  Package,
  Truck,
  MessageSquare,
  ChevronRight,
  AlertTriangle,
  Check,
  Loader2,
  Search,
  UserPlus
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { OperationalLocation } from '../../lib/types';
import { EXCLUDE_REFERENTI } from '../../lib/utils/contactPollution';
import { productSearchFilter } from '../../lib/utils/productSearch';
import { ClientQuickInput, type QuickClientData } from '../clients/ClientQuickInput';
import { CreateClientDrawer } from '../clients/create-drawer/CreateClientDrawer';
import { useClientStore } from '../../lib/stores/clientStore';
import { useManualOrderStore, useDraftTotals, useAllItemsAvailable } from '../../lib/stores/manualOrderStore';
import { ManualOrderService } from '../../lib/services/manualOrderService';
import { OrderProductSelector } from './OrderProductSelector';
import { useProductCommercialNames } from '../../lib/hooks/useProductCommercialNames';
import { formatQuantity } from '../../lib/utils/quantityFormat';
import type { OrderAddress } from '../../lib/types/orders';

interface Client {
  id: string;
  business_name: string;
  email?: string;
  phone?: string;
  vat_number?: string;
  street_address?: string;
  city?: string;
  postal_code?: string;
  province?: string;
  country?: string;
  default_price_list_id?: string;
  price_list_name?: string;
}

interface ManualOrderCreateModalProps {
  organizationId: string;
  userId: string;
  onClose: () => void;
  onSuccess: (orderId: string) => void;
  preselectedClientId?: string;
  /** Modifica di un ordine in bozza esistente invece della creazione */
  editOrderId?: string;
  editOrderNumber?: string;
  /** Ordine nato da un'offerta: cliente e prodotti si aggiornano dall'offerta, qui solo spedizione e note */
  editFromDeal?: boolean;
}

type Step = 'client' | 'products' | 'shipping' | 'notes' | 'review';

export function ManualOrderCreateModal({
  organizationId,
  userId,
  onClose,
  onSuccess,
  preselectedClientId,
  editOrderId,
  editOrderNumber,
  editFromDeal = false
}: ManualOrderCreateModalProps) {
  const orderSettings = customerOrderSettings(useSettingsStore(s => s.settings));
  const { hasPermission } = usePermissions();
  const [priceLists, setPriceLists] = useState<any[]>([]);
  const isEdit = !!editOrderId;
  // Ordine da offerta: niente passi Cliente/Prodotti (si aggiornano dall'offerta)
  const stepOrder: Step[] = isEdit && editFromDeal
    ? ['shipping', 'notes', 'review']
    : ['client', 'products', 'shipping', 'notes', 'review'];
  const [currentStep, setCurrentStep] = useState<Step>(stepOrder[0]);
  const [savingEdit, setSavingEdit] = useState(false);
  // Bozza dell'ordine caricata: prima di allora draftOrder può essere quella di una sessione precedente
  const [editDraftReady, setEditDraftReady] = useState(!editOrderId);
  const [editError, setEditError] = useState<string | null>(null);
  // In modifica la sede di spedizione salvata sull'ordine non va sovrascritta
  // dalla sede predefinita del cliente quando il cliente viene ripreso
  const keepOrderLocationRef = React.useRef(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [sameAsShipping, setSameAsShipping] = useState(true);
  // Indirizzi di spedizione del cliente (client_operational_locations)
  const [clientLocations, setClientLocations] = useState<OperationalLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');

  useEffect(() => {
    if (!selectedClient) { setClientLocations([]); setSelectedLocationId(''); return; }
    let cancelled = false;
    supabase
      .from('client_operational_locations')
      .select('id, client_id, organization_id, name, country, address, city, province, postal_code, region, is_active, notes, is_default, recipient_name, phone, location_type, created_at, updated_at')
      .eq('client_id', selectedClient.id)
      .eq('is_active', true)
      .order('is_default', { ascending: false })
      .order('name', { ascending: true })
      .then(({ data }) => {
        if (cancelled) return;
        const list = (data || []) as OperationalLocation[];
        setClientLocations(list);
        if (keepOrderLocationRef.current) {
          keepOrderLocationRef.current = false;
          const savedId = useManualOrderStore.getState().draftOrder?.shipping_location_id || '';
          setSelectedLocationId(savedId && list.some(l => l.id === savedId) ? savedId : '');
          return;
        }
        const def = list.find(l => l.is_default);
        if (def) applyClientLocation(def.id, list);
        else setSelectedLocationId('');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.id]);

  const applyClientLocation = (locationId: string, list: OperationalLocation[] = clientLocations) => {
    setSelectedLocationId(locationId);
    updateDraft({ shipping_location_id: locationId || null });
    if (!locationId) {
      const billingAddress = useManualOrderStore.getState().draftOrder?.billing_address;
      if (billingAddress) setShippingAddress(billingAddress);
      setSameAsShipping(true);
      return;
    }
    const loc = list.find(l => l.id === locationId);
    if (!loc) return;
    setSameAsShipping(false);
    setShippingAddress({
      company: loc.recipient_name || selectedClient?.business_name,
      address_1: loc.address || undefined,
      city: loc.city || undefined,
      postcode: loc.postal_code || undefined,
      state: loc.province || undefined,
      country: loc.country || selectedClient?.country || 'IT',
      email: selectedClient?.email,
      phone: loc.phone || selectedClient?.phone,
    });
  };
  const [showQuickClientInput, setShowQuickClientInput] = useState(false);
  // "Anagrafica completa" dal form veloce: drawer Notion-like (stesso di Clienti e Offerte)
  const [showFullClientForm, setShowFullClientForm] = useState(false);
  const [fullClientInitial, setFullClientInitial] = useState<Record<string, any> | null>(null);

  // Store hooks
  const {
    draftOrder,
    loading,
    error,
    availabilityResults,
    initEmptyDraft,
    updateDraft,
    addItem,
    updateItem,
    removeItem,
    setBillingAddress,
    setShippingAddress,
    copyBillingToShipping,
    checkAvailability,
    createOrder,
    clearDraft
  } = useManualOrderStore();
  // Nome commerciale accanto al nome magazzino nel riepilogo (solo org con «Nome commerciale» attivo)
  const { commercialNameFor } = useProductCommercialNames((draftOrder?.items || []).map(i => i.product_id));

  const totals = useDraftTotals();
  const allItemsAvailable = useAllItemsAvailable();

  // Load clients
  useEffect(() => {
    let cancelled = false;
    const loadClients = async () => {
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('id, business_name, email, phone, vat_number, street_address, city, postal_code, province, country, default_price_list_id, default_price_list:price_lists!default_price_list_id(name)')
          .eq('organization_id', organizationId)
          .or(EXCLUDE_REFERENTI)
          .order('business_name');

        if (cancelled) return;
        if (error) throw error;
        const mapped = (data || []).map((c: any) => ({
          ...c,
          price_list_name: c.default_price_list?.name || undefined,
          default_price_list: undefined,
        }));
        setClients(mapped);

        const lists = await supabase.from('price_lists').select('id,name,is_default').eq('organization_id', organizationId).eq('is_active', true).order('name');
        if (cancelled) return;
        if (lists.error) throw lists.error;
        setPriceLists(lists.data || []);
        // Fetch directly: the requested client may be beyond the list pagination cap.
        // Pre-select client if provided
        if (preselectedClientId) {
          const direct = await supabase.from('clients').select('*').eq('organization_id', organizationId).eq('id', preselectedClientId).single();
          if (cancelled) return;
          if (direct.error) throw direct.error;
          const client = direct.data;
          if (client && !isEdit) {
            handleSelectClient(client);
            updateDraft({ price_list_id: client.default_price_list_id || lists.data?.find(x => x.is_default)?.id || null });
            setCurrentStep('products');
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading clients:', error);
        setEditError(error instanceof Error ? error.message : 'Impossibile caricare il cliente o i listini');
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    };

    loadClients();
    return () => { cancelled = true; };
  }, [organizationId, preselectedClientId]);

  // Ricerca sul database: l'elenco iniziale si ferma a 1000 clienti (limite PostgREST), quindi con
  // organizzazioni piu' grandi quelli in fondo all'alfabeto non comparivano mai, nemmeno cercandoli.
  const [searchResults, setSearchResults] = useState<Client[] | null>(null);
  useEffect(() => {
    const term = clientSearch.trim();
    // risultati della ricerca precedente via subito: nel frattempo vale il filtro sull'elenco caricato
    setSearchResults(null);
    if (!term) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, business_name, email, phone, vat_number, street_address, city, postal_code, province, country, default_price_list_id, default_price_list:price_lists!default_price_list_id(name)')
        .eq('organization_id', organizationId)
        .or(EXCLUDE_REFERENTI)
        .or(productSearchFilter(term, ['business_name', 'email', 'vat_number']))
        .order('business_name')
        .limit(50);
      if (cancelled || error) return;
      setSearchResults((data || []).map((c: any) => ({ ...c, price_list_name: c.default_price_list?.name || undefined, default_price_list: undefined })));
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [clientSearch, organizationId]);

  // Filter clients based on search (subito sull'elenco caricato, poi i risultati del database)
  const filteredClients = searchResults ?? clients.filter(client => {
    const searchLower = clientSearch.toLowerCase();
    return (
      client.business_name.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.vat_number?.toLowerCase().includes(searchLower)
    );
  });

  // Modifica: carica l'ordine nella bozza. Non si usa initDraftFromOrder dello
  // store: accende il loading globale e il dettaglio ordine, che mostra lo
  // spinner durante il loading, smonterebbe e rimonterebbe questo form in ciclo.
  useEffect(() => {
    if (!editOrderId) return;
    let cancelled = false;
    ManualOrderService.getOrderById(editOrderId, organizationId)
      .then(order => {
        if (cancelled) return;
        initEmptyDraft(order.client_id);
        updateDraft({
          price_list_id: order.price_list_id,
          billing_address: order.billing_address,
          shipping_address: order.shipping_address,
          shipping_location_id: order.shipping_location_id ?? null,
          shipping_method: order.shipping_method,
          shipping_cost: order.shipping_cost || 0,
          customer_notes: order.customer_notes,
          internal_notes: order.internal_notes,
          items: (order.items || []).map(item => ({
            product_id: item.product_id,
            variation_id: item.variation_id,
            product_name: item.product_name,
            product_sku: item.product_sku,
            quantity: item.quantity,
            unit_price: item.unit_price,
            sale_unit: item.sale_unit,
            pack_weight_kg: item.pack_weight_kg,
            discount_amount: item.discount_amount,
            tax_rate: item.tax_rate,
            notes: item.notes,
            line_type: item.line_type === 'custom' ? 'custom' : undefined,
          })),
        });
        setEditDraftReady(true);
      })
      .catch(err => {
        if (!cancelled) setEditError(err instanceof Error ? err.message : "Ordine non caricato");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOrderId, organizationId]);

  // Modifica: quando clienti e bozza sono pronti riprende il cliente dell'ordine
  // (una volta sola, senza azzerare righe e indirizzi come fa la scelta a mano)
  const editClientRestoredRef = React.useRef(false);
  useEffect(() => {
    if (!isEdit || !editDraftReady || editClientRestoredRef.current || loadingClients || !draftOrder?.client_id) return;
    editClientRestoredRef.current = true;
    const restore = (client: Client) => {
      keepOrderLocationRef.current = true;
      setSelectedClient(client);
      setSameAsShipping(JSON.stringify(draftOrder.billing_address || {}) === JSON.stringify(draftOrder.shipping_address || {}));
    };
    const client = clients.find(c => c.id === draftOrder.client_id);
    if (client) { restore(client); return; }
    // L'elenco clienti si ferma a 1000 righe (limite PostgREST): con più clienti
    // quello dell'ordine può non esserci, e senza cliente non si caricano i suoi
    // indirizzi di spedizione. Lo si legge direttamente.
    supabase
      .from('clients')
      .select('id, business_name, email, phone, vat_number, street_address, city, postal_code, province, country, default_price_list_id, default_price_list:price_lists!default_price_list_id(name)')
      .eq('id', draftOrder.client_id)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        const c = { ...(data as any), price_list_name: (data as any).default_price_list?.name || undefined, default_price_list: undefined } as Client;
        setClients(prev => (prev.some(x => x.id === c.id) ? prev : [...prev, c]));
        restore(c);
      });
  }, [isEdit, editDraftReady, loadingClients, clients, draftOrder]);

  // Handle client selection
  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    if (isEdit) {
      // In modifica il cambio cliente non deve svuotare i prodotti già inseriti
      updateDraft({ client_id: client.id, price_list_id: client.default_price_list_id || draftOrder?.price_list_id });
    } else {
      initEmptyDraft(client.id);
      updateDraft({ price_list_id: client.default_price_list_id || priceLists.find(x => x.is_default)?.id || null });
    }
    // Listino predefinito del cliente → usato per prezzare i prodotti e salvato sull'ordine
    if (!isEdit && client.default_price_list_id) {
      updateDraft({ price_list_id: client.default_price_list_id });
    }

    // Set billing address from client
    const billingAddress: OrderAddress = {
      company: client.business_name,
      address_1: client.street_address,
      city: client.city,
      postcode: client.postal_code,
      state: client.province,
      country: client.country || 'IT',
      email: client.email,
      phone: client.phone
    };
    setBillingAddress(billingAddress);
    if (sameAsShipping) {
      setShippingAddress(billingAddress);
    }
  };

  // Handle quick client creation
  const handleQuickClientSubmit = async (data: QuickClientData) => {
    const { data: newClient, error } = await supabase
      .from('clients')
      .insert({
        organization_id: organizationId,
        business_name: data.business_name,
        entity_type: data.entity_type,
        email: data.email || null,
        phone: data.phone || null,
        whatsapp_number: data.whatsapp_number || null,
        stato_cliente: data.stato_cliente || null,
      })
      .select('id, business_name, email, phone, vat_number, street_address, city, postal_code, province, country')
      .single();

    if (error) throw error;

    setClients(prev => [...prev, newClient].sort((a, b) => a.business_name.localeCompare(b.business_name)));
    setShowQuickClientInput(false);
    setClientSearch(newClient.business_name);
    handleSelectClient(newClient);
  };

  // Anagrafica completa: crea il cliente col drawer e lo seleziona nell'ordine
  const handleFullClientSubmit = async (clientData: Record<string, any>) => {
    const created = await useClientStore.getState().createClient(
      { ...clientData, assigned_to: clientData.assigned_to || userId },
      { skipWorkflowTrigger: true },
    );
    if (!created) return false;
    const { data: c } = await supabase
      .from('clients')
      .select('id, business_name, email, phone, vat_number, street_address, city, postal_code, province, country')
      .eq('id', created.id)
      .single();
    if (c) {
      setClients(prev => [...prev.filter(x => x.id !== c.id), c].sort((a, b) => a.business_name.localeCompare(b.business_name)));
      setClientSearch(c.business_name);
      handleSelectClient(c);
    }
    setShowFullClientForm(false);
    setFullClientInitial(null);
    return created;
  };

  // Handle step navigation
  const handleNextStep = async () => {
    const steps = stepOrder;
    const currentIndex = steps.indexOf(currentStep);

    // Check availability before moving to review
    if (currentStep === 'notes' && draftOrder?.items.length) {
      await checkAvailability(organizationId);
    }

    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const handlePrevStep = () => {
    const steps = stepOrder;
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  // Handle order creation
  const handleCreateOrder = async () => {
    const orderId = await createOrder(organizationId, userId);
    if (orderId) {
      onSuccess(orderId);
    }
  };

  // Salvataggio modifiche su ordine in bozza esistente (servizio diretto: il
  // loading globale dello store smonterebbe il dettaglio durante il salvataggio)
  const handleSaveEdit = async () => {
    if (!editOrderId || !draftOrder) return;
    setSavingEdit(true);
    setEditError(null);
    const ctx = { organizationId, userId };
    try {
      if (orderSettings.enabled && !editFromDeal) {
        const {error} = await coreCall('orders/manual', {body:{...draftOrder,order_id:editOrderId,request_id:draftOrder.request_id||crypto.randomUUID()}});
        if (error) throw new Error(error.message);
        clearDraft(); onSuccess(editOrderId); return;
      }
      await ManualOrderService.updateOrder(editOrderId, {
        ...(editFromDeal ? {} : { client_id: draftOrder.client_id, price_list_id: draftOrder.price_list_id ?? null }),
        billing_address: draftOrder.billing_address,
        shipping_address: draftOrder.shipping_address,
        shipping_location_id: draftOrder.shipping_location_id ?? null,
        shipping_method: draftOrder.shipping_method,
        shipping_cost: draftOrder.shipping_cost,
        customer_notes: draftOrder.customer_notes,
        internal_notes: draftOrder.internal_notes,
      }, ctx);
      if (editFromDeal) {
        // Righe invariate: solo i totali, per le spese di spedizione
        await ManualOrderService.recalculateOrderTotals(editOrderId);
      } else {
        await ManualOrderService.updateOrderItems(editOrderId, draftOrder.items, ctx);
      }
      clearDraft();
      onSuccess(editOrderId);
    } catch (err) {
      console.error('Errore salvataggio modifiche ordine:', err);
      setEditError(err instanceof Error ? err.message : 'Errore nel salvataggio delle modifiche');
    } finally {
      setSavingEdit(false);
    }
  };

  useDrawerEscape(true,()=>{if(!loading&&!savingEdit)handleClose();},80);

  // Handle close
  const handleClose = () => {
    clearDraft();
    onClose();
  };

  // Check if can proceed to next step
  const canProceed = (): boolean => {
    switch (currentStep) {
      case 'client':
        return !!selectedClient;
      case 'products':
        return (draftOrder?.items.length ?? 0) > 0;
      case 'shipping':
        return true; // Shipping is optional
      case 'notes':
        return true; // Notes are optional
      case 'review':
        return allItemsAvailable;
      default:
        return false;
    }
  };

  // Step indicator
  const steps: { key: Step; label: string; icon: React.ReactNode }[] = ([
    { key: 'client', label: 'Cliente', icon: <User className="w-4 h-4" /> },
    { key: 'products', label: 'Prodotti', icon: <Package className="w-4 h-4" /> },
    { key: 'shipping', label: 'Spedizione', icon: <Truck className="w-4 h-4" /> },
    { key: 'notes', label: 'Note', icon: <MessageSquare className="w-4 h-4" /> },
    { key: 'review', label: 'Riepilogo', icon: <FileText className="w-4 h-4" /> }
  ] as { key: Step; label: string; icon: React.ReactNode }[]).filter(s => stepOrder.includes(s.key));

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {isEdit ? `Modifica ordine${editOrderNumber ? ` ${editOrderNumber}` : ''}` : 'Nuovo ordine'}
            </h2>
            {selectedClient && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {selectedClient.business_name}
                {selectedClient.price_list_name && (
                  <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
                    {selectedClient.price_list_name}
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.key}>
                <button
                  onClick={() => index <= currentStepIndex && setCurrentStep(step.key)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                    currentStep === step.key
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : index < currentStepIndex
                      ? 'text-green-600 dark:text-green-400 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                  disabled={index > currentStepIndex}
                >
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium ${
                    currentStep === step.key
                      ? 'bg-blue-600 text-white'
                      : index < currentStepIndex
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    {index < currentStepIndex ? <Check className="w-3 h-3" /> : index + 1}
                  </span>
                  <span className="text-sm font-medium hidden sm:inline">{step.label}</span>
                </button>
                {index < steps.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Step: Client */}
          {currentStep === 'client' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                  Seleziona cliente
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Scegli il cliente per cui creare l'ordine
                </p>
              </div>

              {/* Search + Nuovo */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Cerca per nome, email o P.IVA..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500"
                  />
                </div>
                <button
                  onClick={() => {
                    setShowQuickClientInput(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors whitespace-nowrap"
                >
                  <UserPlus className="w-4 h-4" />
                  Nuovo
                </button>
              </div>

              {/* Quick client creation form */}
              {showQuickClientInput && (
                <div className="p-4 border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg">
                  <ClientQuickInput
                    onSubmit={handleQuickClientSubmit}
                    onCancel={() => setShowQuickClientInput(false)}
                    onExpandForm={(quick) => {
                      setShowQuickClientInput(false);
                      setFullClientInitial({ ...quick });
                      setShowFullClientForm(true);
                    }}
                    autoFocus
                    initialName={clientSearch}
                  />
                </div>
              )}

              <CreateClientDrawer
                isOpen={showFullClientForm}
                onClose={() => { setShowFullClientForm(false); setFullClientInitial(null); }}
                initialData={fullClientInitial || { business_name: clientSearch }}
                onComplete={handleFullClientSubmit}
              />

              {/* Client list (hidden when quick input is open) */}
              {!showQuickClientInput && (
              <div className="max-h-80 overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                {loadingClients ? (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
                    Caricamento clienti...
                  </div>
                ) : filteredClients.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    <p>Nessun cliente trovato</p>
                    {!showQuickClientInput && (
                      <button
                        onClick={() => setShowQuickClientInput(true)}
                        className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        + Crea nuovo cliente
                      </button>
                    )}
                  </div>
                ) : (
                  filteredClients.map(client => (
                    <div
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                      className={`p-4 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                        selectedClient?.id === client.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            {client.business_name}
                            {client.price_list_name && (
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-normal">
                                {client.price_list_name}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {client.email} {client.vat_number && `| P.IVA: ${client.vat_number}`}
                          </div>
                        </div>
                        {selectedClient?.id === client.id && (
                          <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              )}

            </div>
          )}

          {/* Step: Products */}
          {currentStep === 'products' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                  Aggiungi prodotti
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Seleziona i prodotti da includere nell'ordine
                </p>
              </div>

              <label className="block text-sm">Listino applicato
                <select className="block border rounded p-2" value={draftOrder?.price_list_id || ''}
                  disabled={!orderSettings.allow_price_list_change || !hasPermission('inventory','update') || !!draftOrder?.items.length}
                  onChange={e => updateDraft({ price_list_id: e.target.value || null })}>
                  <option value="">Prezzi base prodotto</option>{priceLists.map(list => <option key={list.id} value={list.id}>{list.name}</option>)}
                </select>
                {!!draftOrder?.items.length && <span className="text-xs text-gray-500">Per cambiare listino, rimuovi prima le righe: i prezzi già concordati restano preservati.</span>}
              </label>
              <OrderProductSelector
                organizationId={organizationId}
                priceListId={draftOrder?.price_list_id || null}
                onPriceListResolved={id => { if (id !== (draftOrder?.price_list_id || null)) updateDraft({ price_list_id: id }); }}
                selectedItems={draftOrder?.items || []}
                onAddItem={addItem}
                onUpdateItem={updateItem}
                onRemoveItem={removeItem}
                availabilityResults={availabilityResults}
              />

              {/* Totals preview */}
              {(draftOrder?.items.length ?? 0) > 0 && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Subtotale</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {totals.subtotal.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Shipping */}
          {currentStep === 'shipping' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                  Indirizzo di Spedizione
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Configura l'indirizzo e le opzioni di spedizione
                </p>
              </div>

              {/* Spedisci a: indirizzi di spedizione salvati sul cliente (Sei Farm, 02/09/2026) */}
              {clientLocations.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Indirizzo di consegna
                  </label>
                  <select
                    value={selectedLocationId}
                    onChange={(e) => applyClientLocation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">Sede legale (indirizzo di fatturazione)</option>
                    {(['shipping', 'operational'] as const).map(type => {
                      const group = clientLocations.filter(l => (l.location_type || 'operational') === type);
                      if (!group.length) return null;
                      return (
                        <optgroup key={type} label={type === 'shipping' ? 'Indirizzi di spedizione' : 'Sedi operative'}>
                          {group.map(loc => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name}{loc.is_default ? ' · predefinito' : ''}{loc.city ? ` · ${loc.city}` : ''}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* Same as billing checkbox */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsShipping}
                  onChange={(e) => {
                    setSameAsShipping(e.target.checked);
                    if (e.target.checked) {
                      copyBillingToShipping();
                    }
                  }}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Usa lo stesso indirizzo di fatturazione
                </span>
              </label>

              {!sameAsShipping && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Via/Indirizzo
                    </label>
                    <input
                      type="text"
                      value={draftOrder?.shipping_address?.address_1 || ''}
                      onChange={(e) => setShippingAddress({
                        ...draftOrder?.shipping_address,
                        address_1: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Città
                    </label>
                    <input
                      type="text"
                      value={draftOrder?.shipping_address?.city || ''}
                      onChange={(e) => setShippingAddress({
                        ...draftOrder?.shipping_address,
                        city: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      CAP
                    </label>
                    <input
                      type="text"
                      value={draftOrder?.shipping_address?.postcode || ''}
                      onChange={(e) => setShippingAddress({
                        ...draftOrder?.shipping_address,
                        postcode: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Provincia
                    </label>
                    <input
                      type="text"
                      value={draftOrder?.shipping_address?.state || ''}
                      onChange={(e) => setShippingAddress({
                        ...draftOrder?.shipping_address,
                        state: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              )}

              {/* Shipping cost */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Costo di spedizione
                </label>
                <div className="relative w-40">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  <input
                    type="number"
                    value={draftOrder?.shipping_cost || 0}
                    onChange={(e) => updateDraft({ shipping_cost: parseFloat(e.target.value) || 0 })}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Shipping method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Metodo di spedizione (opzionale)
                </label>
                <input
                  type="text"
                  value={draftOrder?.shipping_method || ''}
                  onChange={(e) => updateDraft({ shipping_method: e.target.value })}
                  placeholder="es. Corriere espresso, Ritiro in sede..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500"
                />
              </div>
            </div>
          )}

          {/* Step: Notes */}
          {currentStep === 'notes' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                  Note
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Aggiungi note per il cliente o note interne
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Note per il cliente
                </label>
                <textarea
                  value={draftOrder?.customer_notes || ''}
                  onChange={(e) => updateDraft({ customer_notes: e.target.value })}
                  placeholder="Note visibili al cliente..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Note interne
                </label>
                <textarea
                  value={draftOrder?.internal_notes || ''}
                  onChange={(e) => updateDraft({ internal_notes: e.target.value })}
                  placeholder="Note visibili solo al team..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500"
                />
              </div>
            </div>
          )}

          {/* Step: Review */}
          {currentStep === 'review' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                  Riepilogo Ordine
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Verifica i dettagli prima di creare l'ordine
                </p>
              </div>

              {/* Client info */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Cliente
                </h4>
                <div className="text-gray-900 dark:text-white font-medium">
                  {selectedClient?.business_name}
                </div>
              </div>

              {/* Products summary */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Prodotti ({draftOrder?.items.length || 0})
                </h4>
                <div className="space-y-2">
                  {draftOrder?.items.map((item, index) => {
                    const availability = availabilityResults.find(a => a.product_id === item.product_id);
                    const hasIssue = availability && !availability.is_available;

                    return (
                      <div key={index} className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900 dark:text-white">
                            {item.product_name}
                            {commercialNameFor(item.product_id) && commercialNameFor(item.product_id) !== item.product_name && (
                              <span className="text-[#787774] dark:text-gray-400"> ({commercialNameFor(item.product_id)})</span>
                            )}
                            {' '}x {formatQuantity(item.quantity, { unit: item.sale_unit, unitDisplay: 'none' })}
                          </span>
                          {hasIssue && (
                            <span className="text-xs text-red-600 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Stock insufficiente
                            </span>
                          )}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400">
                          {((item.quantity * item.unit_price) - (item.discount_amount || 0)).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Availability warning */}
              {!allItemsAvailable && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Attenzione: alcuni prodotti non sono disponibili</span>
                  </div>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    Modifica le quantità o rimuovi i prodotti non disponibili per procedere
                  </p>
                </div>
              )}

              {/* Totals */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase mb-3">
                  Totali
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Subtotale</span>
                    <span className="text-gray-900 dark:text-white">
                      {totals.subtotal.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                    </span>
                  </div>
                  {totals.discount_total > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Sconti</span>
                      <span className="text-red-600 dark:text-red-400">
                        -{totals.discount_total.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                      </span>
                    </div>
                  )}
                  {totals.shipping_cost > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Spedizione</span>
                      <span className="text-gray-900 dark:text-white">
                        {totals.shipping_cost.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">IVA</span>
                    <span className="text-gray-900 dark:text-white">
                      {totals.tax_total.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="font-medium text-gray-900 dark:text-white">Totale</span>
                    <span className="font-bold text-lg text-gray-900 dark:text-white">
                      {totals.total.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <button
            onClick={currentStep === stepOrder[0] ? handleClose : handlePrevStep}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
          >
            {currentStep === stepOrder[0] ? 'Annulla' : 'Indietro'}
          </button>

          {editError && <span className="text-sm text-red-600 dark:text-red-400 px-2">{editError}</span>}

          {currentStep === 'review' ? (
            isEdit ? (
              <button
                onClick={handleSaveEdit}
                disabled={!canProceed() || savingEdit || !draftOrder}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {savingEdit && <Loader2 className="w-4 h-4 animate-spin" />}
                Salva modifiche
              </button>
            ) : (
            <button
              onClick={handleCreateOrder}
              disabled={!canProceed() || loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Crea Ordine
            </button>
            )
          ) : (
            <button
              onClick={handleNextStep}
              disabled={!canProceed()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Avanti
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManualOrderCreateModal;
